import { MapPin, ArrowRight } from "lucide-react";
import { heroImage } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { usePhotos } from "@/hooks/usePhotos";

export default function Hero() {
  const { content } = useContent();
  const { photos, loaded } = usePhotos();

  const heroes = photos.filter((p) => p.category === "hero");
  // Show the latest hero photo if uploaded.
  // Until the photos fetch completes, show NOTHING (solid dark bg) instead of
  // flashing a placeholder image.
  const uploaded = heroes.length > 0 ? heroes[heroes.length - 1].data_url : null;
  const bgUrl = uploaded || (loaded ? heroImage : null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[92vh] w-full flex items-end overflow-hidden bg-[#232E26]"
    >
      {bgUrl && (
        <img
          src={bgUrl}
          alt="Mirabella Anemoessa villa pool"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
      )}
      <div className="absolute inset-0 hero-overlay" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-20 md:pb-28 w-full">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-white/90 mb-6">
            <MapPin size={14} />
            <span
              data-testid="hero-overline"
              className="overline text-white/90"
              style={{ color: "rgba(255,255,255,0.92)" }}
            >
              {content.location}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
          >
            Mirabella <em className="not-italic">Anemoessa</em>
          </h1>

          <p
            data-testid="hero-tagline"
            className="mt-6 text-white/90 text-lg md:text-xl max-w-xl leading-relaxed"
          >
            {content.shortDescription}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-inquire"
              onClick={() => scrollTo("inquire")}
              className="btn-terra inline-flex items-center gap-2 px-7 py-4 rounded-sm text-sm tracking-wide uppercase"
            >
              Check availability <ArrowRight size={16} />
            </button>
            <button
              data-testid="hero-cta-gallery"
              onClick={() => scrollTo("gallery")}
              className="text-white border border-white/40 hover:bg-white/10 px-7 py-4 rounded-sm text-sm tracking-wide uppercase transition-colors"
            >
              View the home
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 inset-x-0 z-10 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-white/80 text-xs tracking-[0.22em] uppercase">
          <span>2 bedrooms + sofa bed · sleeps 6</span>
          <span>50m to the sea</span>
          <span>communal pool · olive garden</span>
        </div>
      </div>
    </section>
  );
}
